import { SupportBottomSheet } from "@Light/components/page/SupportBottomSheet";
import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { useScaffold } from "@Light/scaffold";
import { formatDisplayDate } from "@Light/utils/dateHelpers";
import { useState } from "react";
import type { RenewCustomerState } from "./flow/types";
import { NoRenewalOffers } from "./NoRenewalOffers";

export const RenewIntroSimple: React.FC<{ onGetStarted: () => void }> = ({
  onGetStarted,
}) => {
  const scaffold = useScaffold();
  const PageBody = scaffold.page.pageBody;
  const Title = scaffold.page.title;
  const Subtitle = scaffold.page.subtitle;

  return (
    <PageBody
      footer={
        <ButtonFooter>
          <Button size="lg" className="w-full" onClick={onGetStarted}>
            Get started
          </Button>
        </ButtonFooter>
      }
    >
      <div className="absolute top-1/2 left-0 w-full -translate-y-1/2 px-4 gap-2 flex flex-col">
        <Title>Lock in your new rate</Title>
        <Subtitle>We’ve prepared new terms for you to accept.</Subtitle>
      </div>
    </PageBody>
  );
};

export type RenewIntroExpirationProps = {
  expirationDate: Date;
  onGetStarted: () => void;
};

export const RenewIntroExpiration: React.FC<RenewIntroExpirationProps> = ({
  expirationDate,
  onGetStarted,
}) => {
  const scaffold = useScaffold();
  const PageBody = scaffold.page.pageBody;
  const Title = scaffold.page.title;
  const Subtitle = scaffold.page.subtitle;
  const [showSupport, setShowSupport] = useState<boolean>(false);

  const formattedExpirationDate = formatDisplayDate(expirationDate);

  return (
    <>
      <PageBody
        footer={
          <ButtonFooter>
            <Button size="lg" className="w-full" onClick={onGetStarted}>
              Get started
            </Button>
            <Button
              buttonType="clear"
              onClick={() => {
                setShowSupport(true);
              }}
            >
              Have a question?
            </Button>
          </ButtonFooter>
        }
      >
        <div className="absolute top-1/2 left-0 w-full -translate-y-1/2 px-4 gap-2 flex flex-col">
          <Title>
            Lock in your new rate before your current contract expires
          </Title>
          <Subtitle>
            Your energy plan's contract ends on {formattedExpirationDate}. We've
            prepared new terms for you to accept.
          </Subtitle>
        </div>
      </PageBody>
      <SupportBottomSheet
        open={showSupport}
        onClose={() => {
          setShowSupport(false);
        }}
      />
    </>
  );
};

export type RenewIntroProps = {
  customerState: RenewCustomerState;
  expirationDate?: Date;
  earlyTerminationFee?: number;
  noOffers?: boolean;
  onLogout?: (reason?: string) => void;
  onGetStarted?: () => void;
};

export const RenewIntro: React.FC<RenewIntroProps> = ({
  customerState,
  expirationDate,
  earlyTerminationFee,
  noOffers,
  onLogout,
  onGetStarted = () => {},
}) => {
  if (noOffers) {
    return (
      <NoRenewalOffers
        onDone={() => {
          onLogout?.("no_renewal_offers_active");
        }}
      />
    );
  }

  if (
    customerState === "renewal_from_fallback" ||
    !expirationDate ||
    earlyTerminationFee === undefined ||
    expirationDate < new Date()
  ) {
    return <RenewIntroSimple onGetStarted={onGetStarted} />;
  }

  return (
    <RenewIntroExpiration
      expirationDate={expirationDate}
      onGetStarted={onGetStarted}
    />
  );
};
